import { useState, useMemo } from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSButton from "../../system-design/DSButton";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import DSModal from "../../system-design/DSModal";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSAutocomplete from "../../system-design/DSAutocomplete";
import DSTextField from "../../system-design/DSTextField";
import DSAvatar from "../../system-design/DSAvatar";
import { useTheme } from "@mui/material";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import DateTimePicker from "../../system-design/DSDatePicker";
import Calendar, { CalendarDay } from "../../CalendarEvent/components/Calendar";
import { PopulatedDraftCollection, DateValue } from "../../../api/collection";
import useSetCollectionEtaMutation from "../hooks/useSetCollectionEtaMutation";
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import collectionApiClient from "../../../api/collection";
import CollectionReviewModal from "./CollectionReviewModal";

const formatEta = (eta: DateValue) => `${eta.day}/${eta.month}/${eta.year}`;

const useCollectionsWithEtaQuery = () =>
  useQuery({
    queryKey: ["draft-collections", "with-eta"],
    queryFn: () =>
      collectionApiClient.getDraftCollections({
        query: "",
        filter: { hasEta: true },
        sort: { createdAt: -1 },
        limit: 200,
        skip: 0,
        returnCreator: true,
      }),
  });

const useDraftCollectionsInfiniteQuery = (
  textSearch: string,
  filter?: Record<string, unknown>,
) => {
  const queryClient = useQueryClient();
  const limit = 20;
  return useInfiniteQuery<PopulatedDraftCollection[], Error>({
    queryKey: ["draft-collections", textSearch, filter],
    queryFn: async ({ pageParam }) => {
      const { skip = 0 } = (pageParam as { skip?: number }) ?? {};
      const res = await collectionApiClient.getDraftCollections({
        query: textSearch,
        skip,
        limit,
        filter: filter ?? {},
        returnCreator: true,
      });
      res.forEach((c) => {
        queryClient.setQueryData(["collection", c.id], c);
      });
      return res;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < limit) return undefined;
      return { skip: allPages.flat().length, limit };
    },
    initialPageParam: { skip: 0, limit },
  });
};

const AddEtaModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { palette } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCollection, setSelectedCollection] =
    useState<PopulatedDraftCollection | null>(null);
  const [etaDate, setEtaDate] = useState<Date>(new Date());

  const { data: pages, isLoading } = useDraftCollectionsInfiniteQuery(
    searchQuery,
    { hasEta: false },
  );
  const collections = pages?.pages.flat();

  const { mutate: setEta, isPending } = useSetCollectionEtaMutation();

  const handleConfirm = () => {
    if (!selectedCollection) return;
    setEta(
      {
        collectionId: selectedCollection.id,
        eta: {
          year: etaDate.getFullYear(),
          month: etaDate.getMonth() + 1,
          day: etaDate.getDate(),
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <DSModal open={open} onClose={onClose}>
      <DSBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          width: 480,
          p: 4,
          borderRadius: 2,
        }}
      >
        <DSTypography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Add ETA to Collection
        </DSTypography>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <DSAutocomplete
            options={collections ?? []}
            getOptionLabel={(o) => o.name}
            value={selectedCollection}
            onChange={(_, v) => setSelectedCollection(v)}
            inputValue={searchQuery}
            onInputChange={(_, v) => setSearchQuery(v)}
            loading={isLoading}
            renderInput={(params) => (
              <DSTextField
                {...params}
                label="Search collections without ETA"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSAvatar
                    src={option.coverUrl}
                    variant="rounded"
                    sx={{ width: 32, height: 32, flexShrink: 0 }}
                  />
                  <DSTypography variant="body2">{option.name}</DSTypography>
                </DSBox>
              </li>
            )}
            noOptionsText={
              searchQuery ? "No collections found" : "Type to search"
            }
          />
          <DSBox>
            <DSTypography
              variant="caption"
              sx={{ color: palette.text.secondary, display: "block", mb: 1 }}
            >
              ETA Date
            </DSTypography>
            <DateTimePicker
              selectedDate={etaDate}
              onChange={setEtaDate}
              minDate={new Date()}
              isToday={(d) => d.toDateString() === new Date().toDateString()}
            />
          </DSBox>
        </DSBox>
        <DSBox
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
        >
          <DSButton variant="outlined" onClick={onClose} disabled={isPending}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedCollection || isPending}
          >
            {isPending ? "Saving..." : "Add ETA"}
          </DSButton>
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

const CollectionCoverName = ({
  collection,
}: {
  collection: PopulatedDraftCollection;
}) => (
  <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <DSAvatar
      src={collection.coverUrl}
      variant="rounded"
      sx={{ width: 36, height: 36 }}
    />
    <DSTypography variant="body2">{collection.name}</DSTypography>
  </DSBox>
);

const EtaListView = ({
  collections,
  onRowClick,
}: {
  collections: PopulatedDraftCollection[];
  onRowClick: (collection: PopulatedDraftCollection) => void;
}) => {
  const columns = [
    { field: "name" as const, label: "Collection" },
    { field: "productPageMetadata" as const, label: "ETA" },
    { field: "creator" as const, label: "Creator" },
  ];

  return (
    <DSBaseTable
      columns={columns}
      data={collections}
      onPressRow={onRowClick}
      rowBuilder={(collection, cols) => (
        <>
          {cols.map((col) => (
            <DSTableCell key={col.field as string}>
              {col.field === "name" ? (
                <CollectionCoverName collection={collection} />
              ) : col.field === "productPageMetadata" ? (
                collection.productPageMetadata?.eta ? (
                  formatEta(collection.productPageMetadata.eta)
                ) : (
                  "-"
                )
              ) : col.field === "creator" ? (
                (collection.creator?.name ?? "N/A")
              ) : null}
            </DSTableCell>
          ))}
        </>
      )}
    />
  );
};

const EtaCalendarView = ({
  collections,
}: {
  collections: PopulatedDraftCollection[];
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const collectionsByDay = useMemo(() => {
    const map: Record<string, PopulatedDraftCollection[]> = {};
    for (const col of collections) {
      const eta = col.productPageMetadata?.eta;
      if (!eta) continue;
      const key = `${eta.year}-${eta.month}-${eta.day}`;
      if (!map[key]) map[key] = [];
      map[key].push(col);
    }
    return map;
  }, [collections]);

  const renderDayContent = (day: CalendarDay) => {
    if (!day.isCurrentMonth) return null;
    const key = `${day.date.getFullYear()}-${day.date.getMonth() + 1}-${day.date.getDate()}`;
    const dayCollections = collectionsByDay[key] ?? [];
    return (
      <>
        {dayCollections.map((col) => (
          <DSBox
            key={col.id}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
          >
            <DSAvatar
              src={col.coverUrl}
              variant="rounded"
              sx={{ width: 18, height: 18, flexShrink: 0 }}
            />
            <DSTypography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {col.name}
            </DSTypography>
          </DSBox>
        ))}
      </>
    );
  };

  return (
    <Calendar
      currentDate={currentDate}
      onDateChange={setCurrentDate}
      renderDayContent={renderDayContent}
      minHeight={100}
    />
  );
};

const CollectionInProgress = () => {
  const [view, setView] = useState<"list" | "calendar">("list");
  const [isAddEtaModalOpen, setIsAddEtaModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] =
    useState<PopulatedDraftCollection | null>(null);

  const { data: collections, isLoading } = useCollectionsWithEtaQuery();

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
        <DSSegmentedTabs
          size="small"
          options={[
            { label: "List", value: "list" },
            { label: "Calendar", value: "calendar" },
          ]}
          value={view}
          onChange={(v) => {
            if (v) setView(v);
          }}
        />
        <DSButton
          variant="contained"
          onClick={() => setIsAddEtaModalOpen(true)}
        >
          Add ETA Collection
        </DSButton>
      </DSBox>

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : !collections?.length ? (
        <DSTypography variant="body1" textAlign="center" sx={{ mt: 4 }}>
          No collections with ETA set.
        </DSTypography>
      ) : view === "list" ? (
        <EtaListView
          collections={collections}
          onRowClick={setEditingCollection}
        />
      ) : (
        <EtaCalendarView collections={collections} />
      )}

      {isAddEtaModalOpen && (
        <AddEtaModal
          open={isAddEtaModalOpen}
          onClose={() => setIsAddEtaModalOpen(false)}
        />
      )}

      {editingCollection && (
        <CollectionReviewModal
          isModalOpen={true}
          onClose={() => setEditingCollection(null)}
          collection={editingCollection}
          hideReviewActions
        />
      )}
    </>
  );
};

export default CollectionInProgress;
